import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import {
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';

import { Stack } from '@/components/layout';
import { Button, SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';

type PlayerProfileNoteEditorSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (payload: { title: string; detail: string }) => Promise<void> | void;
};

export function PlayerProfileNoteEditorSheet({
  open,
  onOpenChange,
  onSave,
}: PlayerProfileNoteEditorSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);

  const closeSheet = () => onOpenChange(false);

  const trimmedTitle = useMemo(() => title.trim(), [title]);
  const trimmedDetail = useMemo(() => detail.trim(), [detail]);

  const isBothFieldsFilled = useMemo(
    () => trimmedTitle.length > 0 && trimmedDetail.length > 0,
    [trimmedDetail.length, trimmedTitle.length],
  );

  const resetFields = () => {
    setTitle('');
    setDetail('');
  };

  const resetAndClose = () => {
    resetFields();
    closeSheet();
  };

  useEffect(() => {
    if (!open) return;
    // New note sheet should start blank each time it opens.
    resetFields();
    setIsSaving(false);
  }, [open]);

  const showSaveProgressPopup = () => {
    if (!isBothFieldsFilled) {
      showAlert({
        title: STRINGS.player.profile.notesContent.alertTitle,
        message: STRINGS.player.profile.notesContent.blankFieldsMessage,
        buttons: [{ text: STRINGS.dismiss }],
      });
      return;
    }
    showAlert({
      title: STRINGS.player.profile.notesContent.saveProgressTitle,
      message: STRINGS.player.profile.notesContent.saveProgressMessage,
      buttons: [
        {
          text: STRINGS.player.profile.notesContent.cancel,
          style: 'cancel',
        },
        {
          text: STRINGS.player.profile.notesContent.save,
          style: 'default',
          onPress: () => {
            // Save requires both title and detail to have at least one character.
            if (!isBothFieldsFilled) {
              showAlert({
                title: STRINGS.player.profile.notesContent.alertTitle,
                message: STRINGS.player.profile.notesContent.blankFieldsMessage,
                buttons: [{ text: STRINGS.dismiss }],
              });
              return;
            }
            (async () => {
              if (isSaving) return;
              setIsSaving(true);
              try {
                await onSave?.({ title: trimmedTitle, detail: trimmedDetail });
                showAlert({
                  message: STRINGS.player.profile.notesContent.saveSuccessMessage,
                  buttons: [{ text: STRINGS.dismiss }],
                });
                resetAndClose();
              } catch {
              } finally {
                setIsSaving(false);
              }
            })();
          },
        },
      ],
    });
  };

  const showNoticePopup = () => {
    showAlert({
      title: STRINGS.player.profile.notesContent.noticeTitle,
      message: STRINGS.player.profile.notesContent.noticeMessage,
      buttons: [
        {
          text: STRINGS.player.profile.notesContent.cancel,
          style: 'cancel',
        },
        {
          text: STRINGS.player.profile.notesContent.continue,
          style: 'default',
          onPress: () => {
            resetAndClose();
          },
        },
      ],
    });
  };

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent
        tabletWidth={scale(375)}
        closeOnBackdropPress={false}
        style={isTablet ? styles.sheet : [styles.sheet, styles.sheetPhone]}>
        <Stack style={styles.sheetContainer}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity onPress={closeSheet} hitSlop={scale(12)} style={styles.backButton}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={24} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.player.profile.header.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.title}>
                {STRINGS.player.profile.notesContent.newNoteTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerRightSpacer} />
          </Stack>

          <Stack style={styles.divider} />

          <KeyboardAwareScrollView
            ref={scrollRef}
            style={styles.bodyScroll}
            contentContainerStyle={styles.bodyScrollContent}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            bottomOffset={scale(12)}
            extraKeyboardSpace={scale(12)}>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder={STRINGS.player.profile.notesContent.titlePlaceholder}
              placeholderTextColor={colors['6']}
              style={styles.titleInput}
              allowFontScaling={false}
              maxLength={100}
            />

            <TextInput
              value={detail}
              onChangeText={setDetail}
              placeholder={STRINGS.player.profile.notesContent.detailPlaceholder}
              placeholderTextColor={colors['6']}
              multiline
              allowFontScaling={false}
              textAlignVertical="top"
              style={styles.detailInput}
              maxLength={500}
            />
          </KeyboardAwareScrollView>

          <Stack direction={isTablet ? 'row' : 'column'} style={styles.footer}>
            <Button disabled={isSaving} onPress={showSaveProgressPopup} style={styles.saveButton}>
              <Button.Text fontSize={scale(10)} style={styles.uppercaseText}>
                {isSaving
                  ? STRINGS.player.profile.notesContent.saving
                  : STRINGS.player.profile.notesContent.save}
              </Button.Text>
            </Button>

            <Button
              disabled={isSaving}
              onPress={showNoticePopup}
              variant="outline"
              style={styles.cancelButton}>
              <Button.Text style={styles.cancelText}>
                {STRINGS.player.profile.notesContent.cancel}
              </Button.Text>
            </Button>
          </Stack>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  sheet: {
    backgroundColor: 'white',
  },
  uppercaseText: {
    textTransform: 'uppercase',
  },
  scrollView: {
    backgroundColor: !isTablet ? 'white' : colors['1'],
  },
  sheetPhone: {
    height: '92%',
  },
  sheetContainer: {
    flex: 1,
  },
  sheetHeader: {
    paddingHorizontal: scale(20),
    paddingTop: scale(35),
    paddingBottom: scale(16),
  },
  backButton: {
    width: scale(45),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  title: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  headerRightSpacer: {
    width: scale(45),
  },
  divider: {
    height: 1,
    backgroundColor: colors['3'],
  },
  bodyContent: {
    flex: 1,
    backgroundColor: isTablet ? colors['1'] : 'white',
    paddingHorizontal: scale(16),
    paddingTop: scale(30),
    paddingBottom: scale(24),
    gap: scale(20),
  },
  bodyScroll: {
    flex: 1,
    backgroundColor: isTablet ? colors['1'] : 'white',
  },
  bodyScrollContent: {
    flexGrow: 1,
    paddingHorizontal: scale(16),
    paddingTop: scale(30),
    paddingBottom: scale(24),
    gap: scale(20),
  },
  titleInput: {
    height: scale(46),
    borderWidth: 1,
    borderColor: colors['5'],
    borderRadius: scale(4),
    paddingHorizontal: scale(10),
    color: colors['10'],
    fontSize: scale(14),
    fontFamily: fontFamilies.inter,
    backgroundColor: 'white',
  },
  detailInput: {
    flex: 1,
    minHeight: isTablet ? scale(320) : scale(339),
    borderWidth: 1,
    borderColor: colors['5'],
    borderRadius: scale(4),
    paddingHorizontal: scale(10),
    paddingTop: scale(10),
    color: colors['10'],
    fontSize: scale(14),
    fontFamily: fontFamilies.inter,
    backgroundColor: 'white',
    textAlignVertical: 'top',
  },
  footer: {
    paddingHorizontal: scale({ phone: 40, tablet: 16 }),
    paddingVertical: scale(16),
    gap: scale(20),
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors['10'],
  },
  saveButton: {
    flex: isTablet ? 1 : undefined,
    paddingVertical: 0,
    paddingHorizontal: 0,
    height: scale(!isTablet ? 30 : 40),
    borderRadius: scale(3),
  },
  cancelButton: {
    paddingVertical: 0,
    paddingHorizontal: 0,
    height: scale(!isTablet ? 30 : 40),
    borderRadius: scale(3),
    borderColor: colors.Blue,
    flex: isTablet ? 1 : undefined,
  },
  cancelText: {
    color: colors.Blue,
    fontSize: scale(10),
    textTransform: 'uppercase',
  },
}));
